/**
 * Deliverables.jsx — top-level "deliverables tab".
 *
 *   • One collapsible folder per project (the user's projects only).
 *   • Each folder lists 6 file types (PDF report, DXF, BOM CSV,
 *     Single-line PDF, Equipment schedule XLSX, Cable schedule CSV).
 *   • Per-folder sort: Name / Date / Type.
 *   • "Update Deliverables" regenerates ALL files in that folder. While
 *     a folder is updating, file-row downloads + right-click actions
 *     within THAT folder are disabled. Other folders stay clickable, so
 *     the user can keep working.
 *   • Right-click on any file → "Update this one" → regenerates only
 *     that file (the rest of the folder stays accessible).
 *
 * Status flow (per file): missing → queued → generating → ready (or failed).
 * The UI polls /deliverables every 2 s while ANY folder has a non-ready
 * row, then stops.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  DownloadSimple, ArrowsClockwise, Folder, FolderOpen, FileText, CaretRight,
  CaretDown, SortAscending, SortDescending, EnvelopeSimple, X, Package,
  Upload,
} from "@phosphor-icons/react";

const SORT_FIELDS = [
  { id: "name", label: "Name" },
  { id: "date", label: "Date" },
  { id: "type", label: "Type" },
];

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}

function statusBadge(status) {
  const map = {
    missing:    { color: "text-gray-500 border-gray-700",  label: "missing"    },
    queued:     { color: "text-yellow-400 border-yellow-700/50",  label: "queued"     },
    generating: { color: "text-cyan-400 border-cyan-700/50 animate-pulse",  label: "generating" },
    ready:      { color: "text-emerald-400 border-emerald-700/50",  label: "ready"      },
    failed:     { color: "text-red-400 border-red-700/50",  label: "failed"     },
  };
  const m = map[status] || map.missing;
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border ${m.color}`} data-testid={`deliv-status-${status}`}>
      {m.label}
    </span>
  );
}

export default function Deliverables() {
  const [projects, setProjects] = useState([]);
  /** Per-folder state shape:
   *    items[]    — latest server snapshot of the 6 deliverable rows
   *    lockAll    — true ONLY while the user pressed "Update Deliverables"
   *                  on this folder. While true, downloads + right-click
   *                  for EVERY file in this folder are disabled. When the
   *                  user right-clicks "Update this one", we leave lockAll
   *                  alone — only the single row is marked queued/generating
   *                  and the rest of the folder stays usable. The mask
   *                  banner ('Updating this folder…') only appears when
   *                  lockAll is true.
   */
  const [folders, setFolders] = useState({});
  const [openIds, setOpenIds] = useState({});   // pid → bool
  const [sort, setSort] = useState({});         // pid → { field, dir }
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, pid, row }
  /** When non-null, the "Send deliverables" modal is shown for that project.
   *  Shape: { pid, name, recipients: string, subject: string, message: string, sending: bool }. */
  const [emailModal, setEmailModal] = useState(null);
  /** Which row's revision-history popover is open. Shape: { pid, type } or null. */
  const [revsOpen, setRevsOpen] = useState(null);
  const pollRef = useRef(null);

  // initial load
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data || []);
      } catch (e) { toast.error("Failed to load projects"); }
    })();
  }, []);

  const fetchFolder = async (pid) => {
    const { data } = await api.get(`/projects/${pid}/deliverables`);
    setFolders((f) => {
      const prev = f[pid] || {};
      const stillInFlight = data.items.some((it) => it.status === "queued" || it.status === "generating");
      return {
        ...f,
        [pid]: {
          items: data.items,
          // only KEEP lockAll if the bulk regen is still progressing; clear once everything settled
          lockAll: (prev.lockAll && stillInFlight) ? true : false,
        },
      };
    });
  };

  // open/close
  const toggleOpen = (pid) => {
    setOpenIds((o) => {
      const nx = { ...o, [pid]: !o[pid] };
      if (nx[pid] && !folders[pid]) fetchFolder(pid);
      return nx;
    });
  };

  // poll while ANY row anywhere is non-terminal (queued / generating)
  useEffect(() => {
    const inFlight = Object.values(folders).some((f) =>
      (f?.items || []).some((it) => it.status === "queued" || it.status === "generating"));
    if (!inFlight) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      for (const [pid, f] of Object.entries(folders)) {
        const anyPending = (f?.items || []).some((it) => it.status === "queued" || it.status === "generating");
        if (anyPending) await fetchFolder(pid);
      }
    }, 2000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [folders]);

  const updateAll = async (pid) => {
    if (folders[pid]?.lockAll) return;
    try {
      await api.post(`/projects/${pid}/deliverables/regenerate`);
      // optimistic — mark every row as queued + folder as lockAll=true
      setFolders((f) => ({
        ...f,
        [pid]: {
          lockAll: true,
          items: (f[pid]?.items || []).map((it) => ({ ...it, status: "queued", error: null })),
        },
      }));
      toast.success("Update started — you can keep working in other folders.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start update");
    }
  };

  const updateOne = async (pid, row) => {
    try {
      await api.post(`/projects/${pid}/deliverables/${row.type}/regenerate`);
      // ONLY this single row is marked queued. Folder lockAll stays untouched
      // so the user can still download other files / right-click other rows.
      setFolders((f) => ({
        ...f,
        [pid]: {
          ...(f[pid] || {}),
          items: (f[pid]?.items || []).map((it) =>
            it.type === row.type ? { ...it, status: "queued", error: null } : it),
        },
      }));
      toast.success(`Regenerating ${row.label}…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start update");
    } finally {
      setCtxMenu(null);
    }
  };

  const download = async (pid, row) => {
    if (folders[pid]?.lockAll) return;       // folder locked only during bulk regen
    if (row.status !== "ready" || !row.id) return;
    try {
      const res = await api.get(`/deliverables/${row.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.filename || `${row.type}.${row.ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setCtxMenu(null);
    }
  };

  const downloadRevision = async (deliv_id, rev) => {
    try {
      const res = await api.get(`/deliverables/${deliv_id}/revisions/${rev}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rev${rev}.bin`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not download revision");
    }
  };

  // v2.41 — per-deliverable template upload. Opens the file picker, posts
  // to /api/templates/upload-dwt with deliverable_type binding so this
  // template overrides the archetype default for THIS row only.
  const uploadTemplateForRow = async (pid, row) => {
    const file = await new Promise((resolve) => {
      const inp = document.createElement("input");
      inp.type = "file";
      inp.accept = ".dwt,.dxf,application/octet-stream";
      inp.onchange = (e) => resolve(e.target.files?.[0] || null);
      inp.click();
    });
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const qs = new URLSearchParams({
        archetype: "eroom",
        deliverable_type: row.type,
        set_default: "true",
        name: `${row.type}_${file.name.replace(/\.[^.]+$/, "")}`,
      });
      await api.post(`/templates/upload-dwt?${qs.toString()}`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Template uploaded for ${row.type}`);
      // Mark this row stale + regenerate so the new template takes effect.
      await api.post(`/projects/${pid}/deliverables/${row.type}/regenerate`);
      toast.info(`Regenerating ${row.type} with new template…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Upload failed: ${e.message}`);
    }
  };

  const sendDeliverables = async () => {
    if (!emailModal) return;
    const recipients = emailModal.recipients
      .split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error("Add at least one recipient email");
      return;
    }
    setEmailModal((m) => ({ ...m, sending: true }));
    try {
      const { data } = await api.post(`/projects/${emailModal.pid}/deliverables/email`, {
        recipients,
        subject: emailModal.subject || null,
        message: emailModal.message || null,
      });
      toast.success(`Sent ${data.included_files.length} file(s) to ${data.sent_to.length} recipient(s)`);
      setEmailModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
      setEmailModal((m) => m ? { ...m, sending: false } : m);
    }
  };

  // sorting per folder
  const sortRows = (pid, rows) => {
    const s = sort[pid] || { field: "type", dir: "asc" };
    const dir = s.dir === "asc" ? 1 : -1;
    const get = (r) => {
      if (s.field === "name") return (r.filename || r.label || "").toLowerCase();
      if (s.field === "date") return r.updated_at || "";
      return r.type;
    };
    return [...rows].sort((a, b) => {
      const av = get(a), bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  };

  const setFolderSort = (pid, field) => {
    setSort((s) => {
      const cur = s[pid] || { field: "type", dir: "asc" };
      const nx = cur.field === field
        ? { field, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" };
      return { ...s, [pid]: nx };
    });
  };

  // close context menu on outside click
  useEffect(() => {
    const off = () => setCtxMenu(null);
    if (ctxMenu) {
      window.addEventListener("click", off);
      return () => window.removeEventListener("click", off);
    }
  }, [ctxMenu]);

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="deliverables" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// Studio</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Deliverables</h1>
        <p className="text-gray-400 mt-2">
          Every project has a folder of generated outputs (PDF · DXF · CSV · XLSX).
          Click <span className="text-[#00E5FF]">Update Deliverables</span> on a folder to regenerate them all, or
          right-click any file to refresh just that one.
        </p>

        <div className="mt-10 border border-white/10 bg-[#12151C]">
          {projects.length === 0 && (
            <div className="p-8 text-center text-gray-400" data-testid="deliv-empty">
              No projects yet. <Link to="/dashboard" className="text-[#00E5FF] hover:underline">Create one</Link>.
            </div>
          )}
          {projects.map((p) => {
            const open = !!openIds[p.id];
            const folder = folders[p.id];
            const lockAll = !!folder?.lockAll;
            const items = folder?.items || [];
            const sorted = sortRows(p.id, items);
            const curSort = sort[p.id] || { field: "type", dir: "asc" };
            return (
              <div key={p.id} className="border-b border-white/10 last:border-b-0" data-testid={`deliv-folder-${p.id}`}>
                {/* folder header */}
                <div className="px-4 py-3 flex items-center gap-3 hover:bg-[#181C25] transition">
                  <button
                    className="text-gray-400 hover:text-white p-1"
                    onClick={() => toggleOpen(p.id)}
                    data-testid={`deliv-folder-toggle-${p.id}`}
                  >
                    {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
                  </button>
                  {open ? <FolderOpen size={20} className="text-[#FFCC00]" /> : <Folder size={20} className="text-[#FFCC00]" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-white truncate">{p.name}</div>
                    <div className="label-tag text-[10px]">{p.panel_type.replace(/_/g, " ")} · {p.code_standard}</div>
                  </div>
                  <button
                    onClick={() => setEmailModal({
                      pid: p.id, name: p.name,
                      recipients: "", subject: "", message: "",
                      sending: false,
                    })}
                    disabled={lockAll}
                    className={`btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1.5 ${lockAll ? "opacity-50 cursor-not-allowed" : ""}`}
                    title="Email every ready deliverable as a single ZIP to chosen recipients"
                    data-testid={`deliv-send-${p.id}`}
                  >
                    <EnvelopeSimple size={14} /> Send
                  </button>
                  <button
                    onClick={() => updateAll(p.id)}
                    disabled={lockAll}
                    className={`btn-ghost text-xs py-1.5 px-3 inline-flex items-center gap-1.5 ${lockAll ? "opacity-50 cursor-not-allowed" : ""}`}
                    title="Regenerate every file in this folder"
                    data-testid={`deliv-update-all-${p.id}`}
                  >
                    <ArrowsClockwise size={14} className={lockAll ? "animate-spin" : ""} />
                    {lockAll ? "Updating…" : "Update Deliverables"}
                  </button>
                </div>

                {/* file list */}
                {open && (
                  <div className="bg-black/30 border-t border-white/10" data-testid={`deliv-folder-body-${p.id}`}>
                    {/* sort controls */}
                    <div className="px-4 py-2 border-b border-white/5 flex items-center gap-3">
                      <span className="label-tag text-[10px]">Sort</span>
                      {SORT_FIELDS.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => setFolderSort(p.id, f.id)}
                          className={`text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1 px-2 py-0.5 border transition
                            ${curSort.field === f.id ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/10 text-gray-400 hover:text-white"}`}
                          data-testid={`deliv-sort-${p.id}-${f.id}`}
                        >
                          {f.label}
                          {curSort.field === f.id && (curSort.dir === "asc" ? <SortAscending size={11} /> : <SortDescending size={11} />)}
                        </button>
                      ))}
                    </div>

                    {/* lockAll mask (Update Deliverables) */}
                    {lockAll && (
                      <div className="px-4 py-2 bg-cyan-900/20 border-b border-cyan-700/30 text-cyan-300 text-xs font-mono flex items-center gap-2"
                            data-testid={`deliv-updating-mask-${p.id}`}>
                        <ArrowsClockwise size={14} className="animate-spin" />
                        Updating this folder — downloads disabled until finished. Other projects unaffected.
                      </div>
                    )}

                    {/* rows */}
                    {sorted.map((row) => {
                      const rowBusy = row.status === "queued" || row.status === "generating";
                      return (
                      <div
                        key={row.type}
                        className={`px-4 py-2 grid grid-cols-12 gap-3 items-center border-b border-white/5 last:border-b-0 text-sm hover:bg-white/[0.02] ${lockAll ? "opacity-60" : ""}`}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setCtxMenu({ x: e.clientX, y: e.clientY, pid: p.id, row });
                        }}
                        data-testid={`deliv-row-${p.id}-${row.type}`}
                      >
                        <div className="col-span-5 flex items-center gap-2 min-w-0">
                          <FileText size={16} className="text-gray-400 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-white truncate flex items-center gap-1.5">
                              <span className="truncate">{row.label}</span>
                              {row.template_id && (
                                <span
                                  className="shrink-0 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-[#FFCC00]/15 text-[#FFCC00] border border-[#FFCC00]/30"
                                  title="Stamped from your default CAD template"
                                  data-testid={`deliv-template-badge-${p.id}-${row.type}`}
                                >
                                  Template
                                </span>
                              )}
                            </div>
                            <div className="font-mono text-[10px] text-gray-500 truncate">{row.filename || `${row.type}.${row.ext}`}</div>
                          </div>
                        </div>
                        <div className="col-span-2 font-mono text-xs text-gray-400">{row.category}</div>
                        <div className="col-span-2 font-mono text-xs text-gray-400">{fmtDate(row.updated_at)}</div>
                        <div className="col-span-1 font-mono text-xs text-gray-400">{fmtBytes(row.size_bytes)}</div>
                        <div className="col-span-1">{statusBadge(row.status)}</div>
                        <div className="col-span-1 flex items-center justify-end gap-1 relative">
                          {(row.revisions && row.revisions.length > 0) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRevsOpen(revsOpen?.pid === p.id && revsOpen?.type === row.type
                                  ? null : { pid: p.id, type: row.type });
                              }}
                              className="px-1.5 py-0.5 text-[10px] font-mono uppercase border border-white/15 text-gray-400 hover:text-[#FFCC00] hover:border-[#FFCC00] transition"
                              title={`${row.revisions.length} earlier revision${row.revisions.length>1?"s":""} saved`}
                              data-testid={`deliv-history-${p.id}-${row.type}`}
                            >
                              v{row.revisions.length + 1}
                            </button>
                          )}
                          <button
                            onClick={() => download(p.id, row)}
                            disabled={lockAll || rowBusy || row.status !== "ready"}
                            title={lockAll ? "Folder is updating" : row.status === "ready" ? "Download" : "Generate this file first"}
                            className="p-1 text-gray-400 hover:text-[#00E5FF] disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`deliv-download-${p.id}-${row.type}`}
                          >
                            <DownloadSimple size={16} />
                          </button>
                          {row.has_logo_sidecar && (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/deliverables/${row.id}/bundle`}
                              title="Download ZIP bundle (DXF + logo.png + README)"
                              className="p-1 text-gray-400 hover:text-[#FF9F0A]"
                              data-testid={`deliv-bundle-${p.id}-${row.type}`}
                              download
                            >
                              <Package size={16} />
                            </a>
                          )}
                          {row.ext === "dxf" && (
                            <button
                              onClick={() => uploadTemplateForRow(p.id, row)}
                              disabled={rowBusy}
                              title="Upload a custom .dwt template for this deliverable (your title block)"
                              className="p-1 text-gray-400 hover:text-[#A78BFA] disabled:opacity-40"
                              data-testid={`deliv-upload-tpl-${p.id}-${row.type}`}
                            >
                              <Upload size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => updateOne(p.id, row)}
                            disabled={rowBusy}
                            title="Update this one"
                            className="p-1 text-gray-400 hover:text-[#FFCC00] disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`deliv-update-one-${p.id}-${row.type}`}
                          >
                            <ArrowsClockwise size={16} className={rowBusy ? "animate-spin" : ""} />
                          </button>
                          {revsOpen?.pid === p.id && revsOpen?.type === row.type && row.revisions && (
                            <div className="absolute right-0 top-8 z-40 bg-[#1A1F29] border border-white/10 shadow-xl py-1 min-w-[220px]"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`deliv-revs-${p.id}-${row.type}`}>
                              <div className="px-3 py-1.5 label-tag text-[10px] border-b border-white/10">
                                {row.revisions.length} earlier rev{row.revisions.length>1?"s":""}
                              </div>
                              {[...row.revisions].reverse().map((r) => (
                                <button
                                  key={r.rev}
                                  onClick={() => { downloadRevision(row.id, r.rev); setRevsOpen(null); }}
                                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#252B36] flex items-center gap-2 font-mono"
                                  data-testid={`deliv-rev-${p.id}-${row.type}-${r.rev}`}
                                >
                                  <span className="text-[#FFCC00]">v{r.rev}</span>
                                  <span className="text-gray-400 flex-1">{fmtBytes(r.size_bytes)}</span>
                                  <span className="text-gray-500 text-[10px]">{fmtDate(r.saved_at)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {row.status === "failed" && row.error && (
                          <div className="col-span-12 text-red-400 text-xs font-mono pl-7 -mt-1">
                            error: {row.error}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right-click context menu */}
      {ctxMenu && (
        <div
          className="fixed z-50 bg-[#1A1F29] border border-white/10 shadow-xl py-1 min-w-[180px]"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          data-testid="deliv-context-menu"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => updateOne(ctxMenu.pid, ctxMenu.row)}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[#252B36] flex items-center gap-2"
            data-testid="deliv-context-update"
          >
            <ArrowsClockwise size={14} /> Update this one
          </button>
          <button
            onClick={() => download(ctxMenu.pid, ctxMenu.row)}
            disabled={ctxMenu.row.status !== "ready" || folders[ctxMenu.pid]?.lockAll}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[#252B36] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deliv-context-download"
          >
            <DownloadSimple size={14} /> Download
          </button>
        </div>
      )}

      {/* Send deliverables modal */}
      {emailModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
              onClick={() => !emailModal.sending && setEmailModal(null)}
              data-testid="deliv-email-modal-overlay">
          <div className="bg-[#12151C] border border-white/10 w-full max-w-xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="deliv-email-modal">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="label-tag text-[10px]">// Send deliverables</div>
                <div className="font-display text-lg font-bold mt-1">{emailModal.name}</div>
              </div>
              <button
                onClick={() => !emailModal.sending && setEmailModal(null)}
                className="text-gray-400 hover:text-white p-1"
                data-testid="deliv-email-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label-tag text-[10px] block mb-1">Recipients (comma-separated)</label>
                <input
                  type="text"
                  value={emailModal.recipients}
                  onChange={(e) => setEmailModal((m) => ({ ...m, recipients: e.target.value }))}
                  placeholder="alice@example.com, bob@company.com"
                  className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="deliv-email-recipients" />
              </div>
              <div>
                <label className="label-tag text-[10px] block mb-1">Subject (optional)</label>
                <input
                  type="text"
                  value={emailModal.subject}
                  onChange={(e) => setEmailModal((m) => ({ ...m, subject: e.target.value }))}
                  placeholder={`[EEngDeS] Deliverables — ${emailModal.name}`}
                  className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
                  data-testid="deliv-email-subject" />
              </div>
              <div>
                <label className="label-tag text-[10px] block mb-1">Message (optional)</label>
                <textarea
                  value={emailModal.message}
                  onChange={(e) => setEmailModal((m) => ({ ...m, message: e.target.value }))}
                  rows={4}
                  placeholder="Latest project deliverables are attached."
                  className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none resize-none"
                  data-testid="deliv-email-message" />
              </div>
              <p className="text-xs text-gray-500 font-mono">
                Bundles every <span className="text-[#00E5FF]">ready</span> deliverable in this folder
                into one ZIP and sends it via Resend. Files that haven&apos;t been generated yet are skipped —
                hit &ldquo;Update Deliverables&rdquo; first if you want a full package.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setEmailModal(null)}
                disabled={emailModal.sending}
                className="btn-ghost text-sm py-2 px-4"
                data-testid="deliv-email-cancel">
                Cancel
              </button>
              <button
                onClick={sendDeliverables}
                disabled={emailModal.sending}
                className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2"
                data-testid="deliv-email-send">
                <EnvelopeSimple size={14} className={emailModal.sending ? "animate-pulse" : ""} />
                {emailModal.sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
